import { useQuery } from '@apollo/client';
import { AssetLocationIotGate } from '../graphql/queries/assetLocationIotGate';
import { SystemIotGate } from '../graphql/queries/systemIotGate';
import { isSlaEnabled } from '../utils/slaEnabled';
import { IotAttachGateResult, resolveIotAttachGate } from '../utils/iotAttachGate';

interface Options {
    kind: 'assetLocation' | 'system';
    id: string | undefined | null;
    /** Extra reason not to ask (e.g. the wizard's edit mode). */
    skip?: boolean;
}

export interface IotAttachGate extends IotAttachGateResult {
    /** The gate query failed and nothing is cached — callers show their load-failed state. */
    error: Error | undefined;
    /** Installed base node of the asset location / system (for the tree jump-off). */
    ownNodeId: string | undefined;
    /** Ids of the ancestors, root first (for the tree jump-off). */
    pathNodeIds: string[];
    refetch: () => void;
}

/**
 * Backend IoT attach gate of an asset location or system (IBM-1039). The query
 * is only sent while the network scan flag is on; with the flag off the result
 * is "allowed" and nothing is requested, so the build keeps working against a
 * gateway without the fields.
 */
export const useIotAttachGate = ({ kind, id, skip }: Options): IotAttachGate => {
    const enabled = isSlaEnabled();
    const query = useQuery(kind === 'system' ? SystemIotGate : AssetLocationIotGate, {
        variables: { id },
        skip: !enabled || !id || !!skip,
        fetchPolicy: 'cache-and-network',
    });
    const entity = kind === 'system' ? query.data?.system : query.data?.assetLocation;
    const asked = enabled && !!id && !skip;
    const pending = asked && query.loading && !query.data;
    const gate = resolveIotAttachGate({
        enabled: asked,
        loaded: !pending,
        canAttachIotConnection: entity?.canAttachIotConnection ?? null,
        effectiveNetworkScan: entity?.effectiveNetworkScan ?? null,
    });
    return {
        ...gate,
        error: asked && !query.data ? query.error : undefined,
        ownNodeId: entity?.installedBaseNode?.id ?? undefined,
        pathNodeIds: ((entity?.installedBaseNode?.pathNodes ?? []) as Array<{ id: string }>).map((n) => n.id),
        refetch: () => { void query.refetch(); },
    };
};
